import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "w-full flex justify-center items-center h-full"
}
const _hoisted_2 = {
  key: 1,
  class: "flex flex-col gap-12"
}
const _hoisted_3 = { class: "flex flex-col gap-4" }
const _hoisted_4 = { class: "text-xl font-bold text-gray-900 px-1" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = {
  key: 0,
  class: "text-red-900 text-right text-sm"
}
const _hoisted_7 = { class: "flex flex-col gap-4" }
const _hoisted_8 = { class: "text-xl font-bold text-gray-900 px-1" }
const _hoisted_9 = { class: "w-full 2xl:w-2/3 settingsCard" }

import { format } from "date-fns";
import { Form, SubmissionHandler } from "vee-validate";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { onBeforeRouteUpdate } from "vue-router";
import * as yup from "yup";
import Field from "@/components/forms/Field.vue";
import LoadingSpinner from "@/components/loading_state/LoadingSpinner.vue";
import {
  useDeleteLogo,
  useLoadCurrentProject,
  useLogoUrl,
  useUpdateCurrentProject,
  useUploadLogo,
} from "@/composables/project";
import { useSaveBeforeLeaveConfirmationModal } from "@/composables/toast";
import { Project } from "@/types/Project";
import LogoUpload from "@/views/settings_project/components/LogoUpload.vue";

const maxCharacterCount = 1000;

type FormValues = {
  name: string;
  country_code: string;
  zip_code: string;
  timezone: string;
  street: string;
  planned_start: string;
  planned_end: string;
  status: string;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'TabGeneral',
  setup(__props) {

const { t } = useI18n();

const { project, isProjectLoading } = useLoadCurrentProject();
const { updateProject, isUpdateError } = useUpdateCurrentProject();
const { logoUrl, isLogoUrlLoading } = useLogoUrl();
const { deleteLogo } = useDeleteLogo();
const { uploadLogo } = useUploadLogo();
const showSaveBeforeLeaveConfirmationModal = useSaveBeforeLeaveConfirmationModal();

const formRef = ref<typeof Form | null>(null);

onBeforeRouteUpdate((to, from, next) => {
  const meta = formRef.value?.meta;

  if (!meta?.dirty) {
    return next();
  }
  showSaveBeforeLeaveConfirmationModal().then((confirmed) => {
    if (confirmed) {
      next();
    }
  });
});

const statusLabels: Record<Project["status"], string> = {
  active: t("admin.general_project_settings.project_status.active"),
  completed: t("admin.general_project_settings.project_status.completed"),
};

const schema = yup.object({
  name: yup
    .string()
    .required(t("admin.general_project_settings.validation_name_required"))
    .max(
      maxCharacterCount,
      t("admin.general_project_settings.validation_max_characters", { maxCharacterCount }),
    ),
  street: yup
    .string()
    .required(t("admin.general_project_settings.validation_street_required"))
    .max(
      maxCharacterCount,
      t("admin.general_project_settings.validation_max_characters", { maxCharacterCount }),
    ),
});

const initialValues = computed(() => {
  if (!project.value) {
    return undefined;
  }
  const formValues: FormValues = {
    name: project.value.name,
    country_code: project.value.country_code,
    zip_code: project.value.zip_code,
    timezone: project.value.timezone,
    street: project.value.street,
    planned_start: format(project.value.planned_start, "dd.MM.yyyy"),
    planned_end: format(project.value.planned_end, "dd.MM.yyyy"),
    status: statusLabels[project.value.status],
  };
  return formValues;
});

const handleSubmit: SubmissionHandler = (genericObject, context) => {
  const values = genericObject as FormValues;

  return updateProject({
    name: values.name,
    street: values.street,
  })
    .then(() => context.resetForm({ values: initialValues.value }))
    .catch(() => {
      // have to catch the error here, otherwise it's propagated as an unhandled error
      // it's already logged by vue-query
      return undefined;
    });
};

return (_ctx: any,_cache: any) => {
  return (_unref(isProjectLoading) || _unref(isLogoUrlLoading))
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(LoadingSpinner)
      ]))
    : (_unref(project))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("h1", _hoisted_4, _toDisplayString(_ctx.$t("admin.project_settings.tab_general")), 1),
            _createVNode(_unref(Form), {
              onSubmit: handleSubmit,
              initialValues: initialValues.value,
              validationSchema: _unref(schema),
              class: "flex flex-col gap-8 w-full 2xl:w-2/3 settingsCard",
              ref_key: "formRef",
              ref: formRef
            }, {
              default: _withCtx(({ isSubmitting, meta }) => [
                _createVNode(Field, {
                  name: "name",
                  label: _unref(t)('admin.general_project_settings.field_name_label')
                }, null, 8, ["label"]),
                _createVNode(Field, {
                  name: "country_code",
                  label: _unref(t)('admin.general_project_settings.field_country_code_label'),
                  readonly: true
                }, null, 8, ["label"]),
                _createVNode(Field, {
                  name: "zip_code",
                  label: _unref(t)('admin.general_project_settings.field_zip_code_label'),
                  readonly: true
                }, null, 8, ["label"]),
                _createVNode(Field, {
                  name: "timezone",
                  label: _unref(t)('admin.general_project_settings.field_timezone_label'),
                  readonly: true
                }, null, 8, ["label"]),
                _createVNode(Field, {
                  name: "street",
                  label: _unref(t)('admin.general_project_settings.field_street_label')
                }, null, 8, ["label"]),
                _createVNode(Field, {
                  name: "planned_start",
                  label: _unref(t)('admin.general_project_settings.field_planned_start_label'),
                  readonly: true
                }, null, 8, ["label"]),
                _createVNode(Field, {
                  name: "planned_end",
                  label: _unref(t)('admin.general_project_settings.field_planned_end_label'),
                  readonly: true
                }, null, 8, ["label"]),
                _createVNode(Field, {
                  name: "status",
                  label: _unref(t)('admin.general_project_settings.field_status_label'),
                  readonly: true
                }, null, 8, ["label"]),
                _createElementVNode("button", {
                  type: "submit",
                  disabled: isSubmitting || !meta.dirty,
                  class: "flex items-center gap-2 rounded-md self-end w-min bg-yellow-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600 disabled:bg-gray-300 disabled:cursor-default"
                }, [
                  _createElementVNode("span", null, _toDisplayString(_unref(t)("buttons.save")), 1),
                  (isSubmitting && meta.valid)
                    ? (_openBlock(), _createBlock(LoadingSpinner, {
                        key: 0,
                        size: "w-4 h-4"
                      }))
                    : _createCommentVNode("", true)
                ], 8, _hoisted_5),
                (_unref(isUpdateError))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_unref(t)("admin.general_project_settings.save_error_message")), 1))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["initialValues", "validationSchema"])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("h1", _hoisted_8, _toDisplayString(_ctx.$t("admin.general_project_settings.logo")), 1),
            _createElementVNode("div", _hoisted_9, [
              _createVNode(LogoUpload, {
                onUpdateFile: _cache[0] || (_cache[0] = ($event: any) => (_unref(uploadLogo)($event))),
                onDeleteFile: _unref(deleteLogo),
                savedLogo: _unref(logoUrl)
              }, null, 8, ["onDeleteFile", "savedLogo"])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
}
}

})